import { useRef, useCallback } from "react";

/**
 * Custom hook for managing scroll-to-bottom functionality and detecting user scroll state
 * @param {Object} options - Configuration options
 * @param {boolean} options.userHasScrolledUp - Whether user has manually scrolled up from bottom
 * @param {Function} options.setUserHasScrolledUp - Function to set user has scrolled up
 * @param {number} [options.threshold=100] - Distance in pixels from bottom to consider "near bottom"
 * @returns {{
 *   scrollContainerRef: import('react').MutableRefObject<HTMLElement | null>,
 *   scrollToBottom: () => void,
 *   isNearBottom: () => boolean,
 *   userHasScrolledUp: boolean,
 *   handleScroll: () => void,
 *   setUserHasScrolledUp: (value: boolean) => void,
 * }}
 */
export const useScrollToBottom = ({
  threshold = 100,
  userHasScrolledUp,
  setUserHasScrolledUp,
} = {}) => {
  const scrollContainerRef = useRef(null);

  /**
   * Scrolls the container to the bottom
   */
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, []);

  /**
   * Check if user is near the bottom of the scroll container
   * @returns {boolean} True if user is near bottom, false otherwise
   */
  const isNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) {
      return true;
    }

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, [threshold]);

  /**
   * Handle scroll events to detect if user has scrolled up
   */
  const handleScroll = useCallback(() => {
    setUserHasScrolledUp(!isNearBottom());
  }, [isNearBottom, setUserHasScrolledUp]);

  return {
    scrollContainerRef,
    scrollToBottom,
    isNearBottom,
    userHasScrolledUp,
    handleScroll,
    setUserHasScrolledUp, // Exposed for manual state control if needed
  };
};
